/**
 * Ob die Navigation (Lebensbereich-Leiste + Sidebar, zusammen `main-layout__nav`)
 * zu sehen ist. Sie kennt genau zwei Zustände — sichtbar oder ausgeblendet, keine
 * Icon-Leiste dazwischen (siehe `docs/adr/0005-navigation-has-two-states.md`).
 *
 * Einzige Quelle ist die Wahl des Users (`uiState.sidebarLeft`, über IPC
 * persistiert). Ein offener Projekt-Canvas räumte die Navigation früher weg,
 * solange er die Fläche beanspruchte, und brauchte dafür einen zweiten
 * Schalter (`manualOverride`), damit ein ⌘B im Canvas überhaupt gegen ihn
 * ankam. Beides ist entfallen. Was bleibt, ist das flüchtige **Weichen**
 * ({@link shouldNavigationYield}): Es liegt nur über der Wahl des Users, und
 * nur, solange es sonst nicht reicht — neben den Aufgaben-Details und, seit
 * #296, neben einer offenen Pinnwand.
 */
export interface NavigationVisibilityInput {
  /** Persistierte Wahl des Users (`uiState.sidebarLeft`). */
  sidebarLeft: boolean
  /**
   * Ob die **Aufgaben-Details** oder eine offene Pinnwand die Fläche gerade
   * brauchen ({@link shouldNavigationYield}). Flüchtig — es überdeckt die Wahl
   * des Users, es ändert sie nicht.
   */
  yieldToDetails?: boolean
}

export function isNavigationVisible({ sidebarLeft, yieldToDetails = false }: NavigationVisibilityInput): boolean {
  return sidebarLeft && !yieldToDetails
}

/**
 * Die **Navigation** zusammen: Lebensbereich-Leiste (48px) + Sidebar (240px) — ein Unit-Test liest beide aus dem
 * SCSS. Bis zum Diff-Audit von #297 stand hier 304 (mit 64px für die Leiste): Die Navigation wich 16px zu früh, und
 * die Pinnwand sagte ihre Breite 16px zu groß voraus.
 */
export const NAVIGATION_WIDTH = 288

/**
 * Was eine **Aufgabenliste** mindestens braucht, um noch eine Liste zu sein —
 * etwas weniger als das kompakte Listen-Fenster (412px), das seinen eigenen
 * Rahmen mitbringt.
 */
export const LIST_MIN_WIDTH = 360

/**
 * Was die **Aufgaben-Details** mindestens brauchen, damit nichts waagerecht
 * rollt. Dieselbe Zahl wie `$content-min` in `TaskDetails.scss` — wer eine
 * ändert, ändert beide.
 */
export const DETAILS_MIN_WIDTH = 624

/**
 * Was der **Canvas** geteilt mindestens hat — `MIN_COLUMN_WIDTH` in `canvasSplitDrag.ts`.
 * Steht hier noch einmal, weil `layouts/` nicht in ein Feature greift; ein Unit-Test hält beide zusammen.
 */
export const CANVAS_MIN_WIDTH = 320

/**
 * Was die **Aufgabenliste** einer Projektseite mindestens braucht, damit im Kopf Icon und Buttons
 * nebeneinander stehen bleiben (#296) — `LIST_MIN_WIDTH` in `canvasSplitDrag.ts`, dort hergeleitet.
 * Neben den Aufgaben-Details meldet die Projektseite dieselbe Zahl (`listMinWidth`).
 */
export const PROJECT_LIST_MIN_WIDTH = 429


/**
 * Was das Fenster um die Seite herum braucht, bei ausgeblendeter Navigation: Karte links und rechts je 16px,
 * Scroll-Bereich 6px Rand + 1px (`MainLayout.scss`, `&__content`, `&__scroll-wrap`). Bei eingeblendeter Navigation
 * bringt die Sidebar den linken Abstand selbst mit — dann sind es 16px weniger.
 */
export const WINDOW_CHROME_WIDTH = 39
const CARD_LEFT_GAP = 16

/** Der 7px-Rand der Liste zum geteilten Canvas (`ProjectPage.scss`, `--canvas-open`) — ohne Canvas fehlt er. */
const LIST_CANVAS_GAP = 7

export interface PageNeedsInput {
  /** Steht der Canvas des aktiven Tabs geteilt neben der Liste? */
  canvasSplit: boolean
  /** Stehen die Aufgaben-Details des aktiven Tabs offen? */
  detailsOpen: boolean
  /** Was die Liste der Seite neben den Details braucht (`setDetailsOpen`); Vorgabe {@link LIST_MIN_WIDTH}. */
  detailsListMinWidth?: number
  /** Was die Liste der Seite immer braucht, wenn mehr als eine Aufgabenliste (`setPageListMinWidth`, Demnächst 560). */
  pageListMinWidth?: number
}

/**
 * Wie breit die Seite (ohne Rahmen) mindestens sein muss: die Liste mit Buttons im Kopf — mehr, solange daneben
 * ein geteilter Canvas oder die Aufgaben-Details stehen (#296).
 */
export function requiredPageWidth({ canvasSplit, detailsOpen, detailsListMinWidth = LIST_MIN_WIDTH, pageListMinWidth = 0 }: PageNeedsInput): number {
  let page = Math.max(PROJECT_LIST_MIN_WIDTH - LIST_CANVAS_GAP, pageListMinWidth)
  if (canvasSplit) page = Math.max(page, PROJECT_LIST_MIN_WIDTH + CANVAS_MIN_WIDTH)
  if (detailsOpen) page = Math.max(page, DETAILS_MIN_WIDTH + detailsListMinWidth)
  return page
}

/**
 * **Wie schmal das Hauptfenster gerade werden darf** (#296, Sven 09.10.2026): so schmal wie die Liste, mit Buttons
 * im Kopf — mehr nur, solange etwas offen ist, das daneben Platz braucht. Öffnet man es in einem schmaleren Fenster,
 * wächst das Fenster mit (`windowManager.setContentMinWidth`). Die Navigation zählt nie: Sie weicht
 * ({@link shouldNavigationYield}).
 *
 * Bis dahin galten feste 1000px — genug für Details und Liste nebeneinander (Abnahme 21.09.2026, F-Q3), aber
 * auch dann, wenn nichts davon offen war.
 *
 * @example
 * mainWindowMinWidth({ canvasSplit: false, detailsOpen: false }) // 461 — nur die Liste
 * mainWindowMinWidth({ canvasSplit: true, detailsOpen: false })  // 788 — Liste + geteilter Canvas
 */
export function mainWindowMinWidth(input: PageNeedsInput): number {
  return WINDOW_CHROME_WIDTH + requiredPageWidth(input)
}

/**
 * Die Breite, an der sich das Weichen misst — oder `null`, solange sie nichts taugt (dann weicht nichts).
 *
 * Im Listen- und Stapel-Modus ist das Fenster ohnehin schmal; dort weicht die Navigation nicht. Beim Wechsel zurück
 * steht der Modus schon auf „voll“, das Fenster aber noch ein paar Bilder auf ~412px — schmaler, als ein volles
 * Hauptfenster je wird. Gemessen daran wiche die Navigation und führe nach dem Vergrößern sichtbar herein
 * (Diff-Audit #296). Im Web gibt es diese Untergrenze nicht, dort zählt jede Breite.
 */
export function settledBodyWidth({ bodyWidth, compact, isElectron }: { bodyWidth: number | null; compact: boolean; isElectron: boolean }): number | null {
  if (compact || bodyWidth == null) return null
  if (isElectron && bodyWidth < mainWindowMinWidth({ canvasSplit: false, detailsOpen: false })) return null
  return bodyWidth
}

export interface NavigationYieldInput {
  /** Stehen die **Aufgaben-Details** des aktiven Tabs offen? */
  detailsOpen: boolean
  /**
   * Steht die Pinnwand des aktiven Tabs offen — **geteilt oder maximiert** (`isCanvasFocused`)? Beide verlangen
   * dasselbe (#297): Weicht die Navigation der geteilten, bleibt sie auch bei der maximierten weg. Anriss und
   * Zuziehen zählen nicht.
   */
  canvasOpen?: boolean
  /** Breite von `.main-layout__body`; `null`, solange nichts gemessen wurde. */
  bodyWidth: number | null
  /**
   * Was die Liste DIESER Seite neben den Details mindestens braucht — gemeldet von der Seite
   * (`setDetailsOpen`). Die Zeitleiste von **Demnächst** braucht mehr als eine Aufgabenliste (#226).
   * Vorgabe {@link LIST_MIN_WIDTH}.
   */
  listMinWidth?: number
  /** Was die Liste der Seite auch ohne Details braucht (Demnächst, #296) — {@link requiredPageWidth}. */
  pageListMinWidth?: number
}

/**
 * **Die Navigation weicht den Aufgaben-Details, wenn es sonst nicht reicht**
 * (Abnahme 21.09.2026, F-Q3).
 *
 * Im engen Fenster deckte die Karte die **Aufgabenliste** vollständig zu: Sie
 * hält ihre 624px, die Liste gibt nach (so gewollt, `TaskDetails.scss`) — bis
 * von ihr nichts mehr übrig war.
 *
 * **Ein Automatismus, und das ist eine Wiederholung.** Der **Canvas** räumte
 * die Navigation früher genauso weg; das ist entfallen, weil es einen zweiten
 * Schalter brauchte, damit ⌘B überhaupt dagegen ankam. Hier ist es anders
 * gebaut: Der persistierte Wert (`sidebarLeft`) wird NICHT angefasst, das
 * Verdrängen liegt nur darüber ({@link isNavigationVisible}). Wer ⌘B drückt,
 * ändert seine Wahl wie immer — und sieht sie, sobald die Details zugehen. Die
 * zwei Zustände aus ADR 0005 bleiben damit zwei.
 *
 * **Seit #296 (Sven, 09.10.2026) weicht sie jeder Seite, die neben ihr ihre
 * Mindestbreite nicht bekäme** ({@link requiredPageWidth}): der Liste allein, damit
 * im Kopf die Buttons stehen bleiben — das Fenster darf jetzt bis auf sie schrumpfen —,
 * der Liste neben einem geteilten Canvas und neben den Details.
 *
 * **Maximiert zählt wie geteilt (#297, Sven 09.10.2026).** Die maximierte Pinnwand braucht zwar keine Liste
 * daneben; ließe sie die Navigation aber zurückkommen, führe die bei jedem Umschalten zwischen geteilt und
 * maximiert herein und wieder hinaus — zwei Bewegungen statt einer, und die Zeile neben der Pinnwand änderte ihre
 * Breite mitten in deren Bewegung.
 *
 * Ohne Messung wird nicht geräumt: Eine Navigation, die beim Aufbau kurz
 * verschwindet, sähe aus wie ein Fehler.
 */
export function shouldNavigationYield({ detailsOpen, canvasOpen = false, bodyWidth, listMinWidth = LIST_MIN_WIDTH, pageListMinWidth }: NavigationYieldInput): boolean {
  if (bodyWidth == null) return false
  const pageBesideNavigation = bodyWidth - NAVIGATION_WIDTH - (WINDOW_CHROME_WIDTH - CARD_LEFT_GAP)
  return pageBesideNavigation < requiredPageWidth({ canvasSplit: canvasOpen, detailsOpen, detailsListMinWidth: listMinWidth, pageListMinWidth })
}

/**
 * **Um wie viel die Seite breiter wird, wenn die Navigation von `visibleNow` auf `visibleAfter` geht** (#297) —
 * negativ, wenn sie zurückkommt. Sie gibt ihre 288px frei, die Inhaltskarte nimmt sich dafür links 16px Rand.
 *
 * Die Pinnwand braucht die Zahl VOR ihrer Bewegung: Sie friert ihren Inhalt auf die Breite ein, die sie am Ende
 * haben wird, und die Navigation fährt im selben Takt hinaus. Gerechnet mit der Zeile von vorher stand der Inhalt
 * 250ms um diese Strecke daneben und rastete im letzten Bild ein.
 */
export function navigationRowGrowth(visibleNow: boolean, visibleAfter: boolean): number {
  if (visibleNow === visibleAfter) return 0
  const freed = NAVIGATION_WIDTH - CARD_LEFT_GAP
  return visibleNow ? freed : -freed
}

/**
 * **Um wie viel die Zeile neben der Pinnwand wächst, wenn sie JETZT aufgeht** (#297) — der Wert, den `MainLayout`
 * der Pinnwand hinterlegt. Verglichen wird die Navigation von jetzt mit der am Ende der Bewegung.
 *
 * Am Ende stehen die **Aufgaben-Details** zu: Wer die Pinnwand öffnet oder umschaltet, schließt sie (`ProjectPage`),
 * aber erst NACH dem Start der Bewegung. War die Navigation ihnen gewichen und reicht der Platz für Liste und
 * Pinnwand, kommt sie während der Bewegung zurück, und die Zeile schrumpft (Diff-Audit #297).
 */
export function rowGrowthOnCanvasOpen({ sidebarLeft, ...input }: NavigationYieldInput & { sidebarLeft: boolean }): number {
  const visibleNow = isNavigationVisible({ sidebarLeft, yieldToDetails: shouldNavigationYield(input) })
  const visibleAfter = isNavigationVisible({
    sidebarLeft,
    yieldToDetails: shouldNavigationYield({ ...input, detailsOpen: false, canvasOpen: true }),
  })
  return navigationRowGrowth(visibleNow, visibleAfter)
}

/**
 * Der Peek — die Navigation als Overlay, solange der Zeiger auf dem Titlebar-Icon
 * oder dem Panel steht — ergibt nur Sinn, wenn gerade nichts zu sehen ist.
 * Exakt die Umkehrung, bewusst als eigene Funktion benannt statt als `!`-Aufruf
 * an den Verwendungsstellen.
 */
export function canPeekNavigation(input: NavigationVisibilityInput): boolean {
  return !isNavigationVisible(input)
}

/**
 * Steht gerade ein Overlay im DOM, das aus der Navigation heraus geöffnet wurde?
 *
 * Radix-Overlays (Kontextmenüs, Tooltips, Popover) rendern
 * ihre Panels per Portal an `document.body` — also AUSSERHALB von `.main-layout__nav`.
 * Der Zeiger, der vom gepeekten Panel zu so einem Menü wandert, löst darum
 * `mouseleave` aus, und ohne diese Prüfung führe die Navigation unter dem offenen
 * Menü weg.
 */
export function hasOpenNavigationOverlay(): boolean {
  return !!document.querySelector('[data-radix-popper-content-wrapper]')
}
